import { LegalLayout } from './LegalLayout'
import { COMPANY } from './legalConfig'

export function Privacy() {
  const mail = <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>

  return (
    <LegalLayout title="Privacy Policy">
      <p className="legal__lede">
        This policy explains what {COMPANY.tradingName} collects, why we collect it, who we share
        it with, and the control you have over it. We have written it in plain language rather
        than boilerplate, because a policy nobody can read protects nobody.
      </p>

      <h2>1. Who we are</h2>
      <p>
        {COMPANY.tradingName} is a daily local news briefing and video scripting service operated
        by {COMPANY.legalName} ("we", "us", "our"). For the purposes of the UK and EU General Data
        Protection Regulation, {COMPANY.legalName} is the data controller for the information
        described in this policy.
      </p>
      <p>
        Our registered address and contact details are on the <a href="/contact">contact page</a>.
        For anything relating to privacy, write to {mail} with "Privacy" in the subject line.
      </p>

      <h2>2. Who this policy covers</h2>
      <p>This policy applies to:</p>
      <ul>
        <li>visitors to {COMPANY.site};</li>
        <li>people who hold a {COMPANY.tradingName} account;</li>
        <li>people who use the {COMPANY.tradingName} iOS or Android app.</li>
      </ul>
      <p>
        {COMPANY.tradingName} accounts are provided to organisations. If you use the service
        through an employer or a publisher, that organisation administers your account and can see
        the content produced under it, including recordings saved to its library.
      </p>

      <h2>3. Information we collect</h2>

      <h3>3.1 Information you give us</h3>
      <ul>
        <li>
          <strong>Account details.</strong> Your email address, your name if you choose to give
          one, and the invite code that links your account to your organisation.
        </li>
        <li>
          <strong>Password.</strong> Handled by our authentication provider and stored only as a
          salted hash. We never see or store your password in readable form.
        </li>
        <li>
          <strong>Preferences.</strong> Your coverage areas, the topics you want more or less of,
          your accent colour, and your light or dark theme choice.
        </li>
        <li>
          <strong>Correspondence.</strong> Messages you send us, so that we can answer them.
        </li>
      </ul>

      <h3>3.2 Information created as you use the service</h3>
      <ul>
        <li>
          <strong>Your brief.</strong> The stories selected for you each day, and whether you
          starred, skipped, or recorded each one.
        </li>
        <li>
          <strong>Recordings.</strong> Video and audio you record in the app, together with basic
          technical details such as duration, file size, and format.
        </li>
        <li>
          <strong>Device tokens.</strong> If you enable notifications, the push token issued by
          Apple or Google for your device, so we can tell you when your brief is ready.
        </li>
      </ul>

      <h3>3.3 Information collected automatically</h3>
      <p>
        Our hosting and infrastructure providers keep standard server logs, which can include IP
        address, browser or device type, and timestamps. These are used to keep the service
        running and secure, to diagnose faults, and to detect abuse. We do not use them to build
        profiles of individuals.
      </p>

      <h2>4. What we do not collect</h2>
      <p>We want to be specific here, because a lot of services say the opposite:</p>
      <ul>
        <li>We do not use analytics packages such as Google Analytics.</li>
        <li>We do not use advertising networks, tracking pixels, or third party cookies.</li>
        <li>We do not sell or rent personal information, and never have.</li>
        <li>We do not build advertising or behavioural profiles.</li>
        <li>
          The only browser storage we use is the session token that keeps you signed in. There is
          no marketing or tracking cookie, which is why you will not see a cookie banner.
        </li>
      </ul>

      <h2>5. How we use information, and our legal basis</h2>
      <table className="legal__table">
        <thead>
          <tr><th>Purpose</th><th>Legal basis (UK and EU GDPR)</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>Creating and administering your account</td>
            <td>Performance of a contract</td>
          </tr>
          <tr>
            <td>Producing and delivering your daily brief</td>
            <td>Performance of a contract</td>
          </tr>
          <tr>
            <td>Storing recordings in your organisation's library</td>
            <td>Performance of a contract</td>
          </tr>
          <tr>
            <td>Keeping the service secure and preventing abuse</td>
            <td>Legitimate interests</td>
          </tr>
          <tr>
            <td>Fixing faults and improving the product</td>
            <td>Legitimate interests</td>
          </tr>
          <tr>
            <td>Sending push notifications</td>
            <td>Consent, which you can withdraw at any time</td>
          </tr>
          <tr>
            <td>Answering your messages</td>
            <td>Legitimate interests</td>
          </tr>
          <tr>
            <td>Meeting legal, tax, and accounting obligations</td>
            <td>Legal obligation</td>
          </tr>
        </tbody>
      </table>

      <h2>6. How your brief is produced, and what the AI sees</h2>
      <p>
        Every night we search publicly available local news sources for the places you cover, rank
        what we find against the topics you have weighted, and use an AI language model to draft a
        first version of each script in a consistent voice.
      </p>
      <p>
        We think you should know exactly what is sent to that model, so:
      </p>
      <ul>
        <li>
          <strong>Sent:</strong> headlines, summaries, and source names from public news articles,
          plus the place names and topic settings for your coverage area.
        </li>
        <li>
          <strong>Not sent:</strong> your recordings, your email address, your name, your password,
          or any other account identifier.
        </li>
      </ul>
      <p>
        Your recordings are never processed by an AI model, and no content you create is used to
        train any model, ours or anyone else's.
      </p>

      <h2>7. Who we share information with</h2>
      <p>
        We do not sell personal information. We share it only with the service providers below,
        each for a specific and limited purpose, and each bound to protect it:
      </p>
      <table className="legal__table">
        <thead>
          <tr><th>Provider</th><th>Purpose</th><th>Data involved</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>Supabase</td>
            <td>Database, authentication, and private file storage</td>
            <td>Account details, briefs, recordings</td>
          </tr>
          <tr>
            <td>Google Cloud and Firebase</td>
            <td>Website hosting and running the nightly brief pipeline</td>
            <td>Server logs, brief content</td>
          </tr>
          <tr>
            <td>Anthropic</td>
            <td>Drafting scripts from public news material</td>
            <td>Public news text and topic settings only</td>
          </tr>
          <tr>
            <td>SerpAPI</td>
            <td>Searching public local news sources</td>
            <td>Place and topic search terms only</td>
          </tr>
          <tr>
            <td>Apple and Google</td>
            <td>Delivering push notifications, if enabled</td>
            <td>Device push token</td>
          </tr>
        </tbody>
      </table>
      <p>We may also disclose information where we are legally required to, specifically:</p>
      <ul>
        <li>to comply with a valid legal process, court order, or regulatory request;</li>
        <li>to establish, exercise, or defend legal claims;</li>
        <li>to protect the rights, safety, or property of our users, the public, or ourselves;</li>
        <li>
          to a buyer or successor if the business is sold or merged, in which case we will notify
          you before your information becomes subject to a different policy.
        </li>
      </ul>

      <h2>8. International transfers</h2>
      <p>
        We are based in the United States and our providers process data there. If you use
        {' '}{COMPANY.tradingName} from the United Kingdom, the European Economic Area, or another
        region with data transfer rules, your information will be transferred to and processed in
        the United States. Where those rules require it, transfers are made under Standard
        Contractual Clauses or another approved safeguard offered by the provider concerned.
      </p>

      <h2>9. How long we keep information</h2>
      <table className="legal__table">
        <thead>
          <tr><th>Information</th><th>Retention</th></tr>
        </thead>
        <tbody>
          <tr><td>Account details</td><td>While your account is open</td></tr>
          <tr><td>Briefs and stories</td><td>While your account is open, so you can review past coverage</td></tr>
          <tr><td>Recordings</td><td>Until you or your organisation delete them</td></tr>
          <tr><td>Device push tokens</td><td>Until the device unregisters or the token is rejected</td></tr>
          <tr><td>Server logs</td><td>Typically 30 to 90 days, per provider defaults</td></tr>
          <tr><td>Correspondence</td><td>Up to 2 years after the matter is closed</td></tr>
        </tbody>
      </table>
      <p>
        When you close your account we delete your personal information within 30 days, except
        where we must keep records for legal, tax, or accounting reasons. Backups are overwritten
        on a rolling cycle, so a copy may persist briefly in backup storage after deletion.
      </p>

      <h2>10. Your rights</h2>
      <p>Wherever you live, you can ask us to:</p>
      <ul>
        <li>give you a copy of the personal information we hold about you;</li>
        <li>correct anything that is wrong or incomplete;</li>
        <li>delete your personal information;</li>
        <li>export your information in a portable format;</li>
        <li>restrict or object to certain processing;</li>
        <li>withdraw consent, for example by turning notifications off.</li>
      </ul>
      <p>
        Email {mail} with "Data request" in the subject line. We will respond within 30 days. We do
        not charge for this and we will not treat you differently for asking.
      </p>

      <h3>10.1 If you are in the United Kingdom or the European Economic Area</h3>
      <p>
        You have the rights above under the UK and EU GDPR, and the right to complain to your
        supervisory authority. In the UK that is the Information Commissioner's Office.
      </p>

      <h3>10.2 If you are in California</h3>
      <p>
        Under the CCPA as amended by the CPRA you have the right to know what personal information
        we collect and why, to request deletion or correction, and to be free from discrimination
        for exercising those rights.
      </p>
      <p>
        <strong>
          We do not sell personal information, and we do not share it for cross context behavioural
          advertising.
        </strong>{' '}
        Because we do neither, there is no "Do Not Sell or Share My Personal Information" link to
        offer. We do not knowingly process the personal information of anyone under 16.
      </p>

      <h2>11. Security</h2>
      <p>We take the following measures, among others:</p>
      <ul>
        <li>all traffic is encrypted in transit using TLS;</li>
        <li>
          recordings are held in private storage with row level access rules that restrict every
          file to the organisation that created it;
        </li>
        <li>passwords are stored only as salted hashes;</li>
        <li>access to production systems is limited to people who need it;</li>
        <li>API credentials are held in managed secret storage, not in application code.</li>
      </ul>
      <p>
        No system is perfectly secure. If we become aware of a breach affecting your personal
        information we will notify you and the relevant regulator without undue delay, and in any
        case within the time limits the law sets.
      </p>

      <h2>12. Children</h2>
      <p>
        {COMPANY.tradingName} is a professional tool for publishers and community journalists. It
        is not directed at children, and we do not knowingly collect personal information from
        anyone under 16. If you believe a child has given us information, contact {mail} and we
        will delete it.
      </p>

      <h2>13. Changes to this policy</h2>
      <p>
        If we change this policy we will update the date shown at the top of this page. Where a
        change materially affects how we handle your information, we will notify account holders
        directly and, where the law requires it, ask for your consent before the change takes
        effect. Previous versions are available on request.
      </p>

      <h2>14. Contact</h2>
      <p>
        Questions, requests, or complaints about privacy can go to {mail}, or by post to the
        registered address on our <a href="/contact">contact page</a>. If you are not satisfied
        with our response you have the right to complain to your data protection authority.
      </p>
    </LegalLayout>
  )
}
